/**
 * The one place a dashboard-initiated write runs (Feature: granular-crud-api, task 14.2;
 * Requirements 11.5, 11.6; Design: §11).
 *
 * Req 11.6 is a statement about state, not about messaging: a failed write must display the returned
 * error *and leave the affected view exactly as it was before the request*. That is easy to satisfy
 * accidentally and easy to break accidentally, so it is expressed structurally here — {@link useWrite}
 * owns only the in-flight flag and the last failure, and it touches the caller's data through a single
 * `onSuccess` callback that runs **only** on success. A page therefore cannot optimistically mutate a
 * list, and a list re-fetch (or a navigation) can only happen after the API has confirmed the write.
 *
 * On failure it also splits the response's per-field errors out of the message ({@link ApiError}'s
 * `fieldErrors`, parsed from a `validation_failed` body) so a form can place each one beside its input
 * (Req 11.5) while the message goes to the form-level panel.
 */

import { useCallback, useState } from 'react';

import type { FieldError } from '@soroush/core';

import { ApiError } from '../api/client';

/** What a form needs to render about the last write attempt. */
export interface WriteState {
  /** A request is in flight; controls disable without the draft being discarded. */
  submitting: boolean;
  /** The failed request's message (Req 11.6); absent when the last attempt did not fail. */
  errorMessage?: string;
  /** Per-field errors from a `validation_failed` rejection (Req 11.5). */
  fieldErrors?: readonly FieldError[];
}

export interface WriteRunner {
  state: WriteState;
  /**
   * Run one write. `onSuccess` is invoked with the response **only** when the request succeeds — it is
   * where a page navigates or re-fetches, so the pre-request view survives a failure untouched.
   */
  run: <T>(operation: () => Promise<T>, onSuccess: (result: T) => void) => Promise<void>;
  /** Clear the recorded failure (e.g. when a confirm dialog is dismissed and reopened). */
  reset: () => void;
}

const IDLE: WriteState = { submitting: false };

export function useWrite(): WriteRunner {
  const [state, setState] = useState<WriteState>(IDLE);

  const run = useCallback(
    async <T,>(operation: () => Promise<T>, onSuccess: (result: T) => void): Promise<void> => {
      setState({ submitting: true });
      try {
        const result = await operation();
        setState(IDLE);
        onSuccess(result);
      } catch (error: unknown) {
        setState({
          submitting: false,
          errorMessage: error instanceof Error ? error.message : 'The request failed',
          ...(error instanceof ApiError && error.fieldErrors !== undefined
            ? { fieldErrors: error.fieldErrors }
            : {}),
        });
      }
    },
    [],
  );

  const reset = useCallback(() => setState(IDLE), []);

  return { state, run, reset };
}
